import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import { ActionStatusGlyph } from "@/accounts-payable/common";
import type { ApAgentAction } from "@/accounts-payable/types";
import {
  actionCategoryLabel,
  actionDetail,
  automatedOutcomeCopy,
} from "@/accounts-payable/utils";

export const AutomatedActionRow = ({
  action,
  showDivider,
}: {
  action: ApAgentAction;
  /** False for the first row — the header band already draws that edge. */
  showDivider: boolean;
}) => {
  const outcome = automatedOutcomeCopy(action);
  // Only an apply failure reads red; other deviations are muted info.
  const isApplyFailure = action.status === "apply_failed";
  return (
    <Stack align="start" gap={0} className="w-full">
      <Stack
        direction="row"
        gap={2}
        align="start"
        justify="between"
        className={cn("w-full", {
          "border-t border-line": showDivider,
        })}
      >
        {/* The outcome glyph leads the row like a checklist mark; the right side
            stays open for per-row actions we may add later (retry, view in
            ServiceTitan). */}
        <Stack
          direction="row"
          gap={2}
          align="center"
          className="min-w-0 flex-1"
        >
          <ActionStatusGlyph
            action={action}
            outline
            className="h-5 w-5 flex-none"
          />
          <Stack align="start" gap={0} className="min-w-0">
            {/* Eyebrow: the action category as a neutral caption on top, the
                specific target below it. One neutral color for every type — a
                per-type hue read as status (red like an error) when it was only
                meant to differentiate. */}
            <span className="text-xs font-semibold text-subtle">
              {actionCategoryLabel(action)}
            </span>
            <span className="text-sm">
              {actionDetail(action)}
              {/* The outcome is appended only when it deviates from a clean
                  apply; only an apply failure reads red. */}
              {outcome && (
                <span
                  className={cn({
                    "text-danger": isApplyFailure,
                    "text-subtle": !isApplyFailure,
                  })}
                >
                  {" · "}
                  {outcome}
                </span>
              )}
            </span>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
